<template>
  <Banner
    v-if="showUpgradeNudge"
    :canCloseBanner="false"
    class="upgradeNudge"
  >
    <template v-slot:content>
      <div
        class="content"
      >
        <div class="textArea">
          <Icon
            class="infoIcon"
            icon="premiumDiscover"
            :scale="1.25"
          />
          <MarkdownInline
            data-test-id="UpgradeNudge__text"
            :mdTemplate="copy"
          />
        </div>
        <div v-if="hasBilling">
          <Button
            color="primary"
            data-test-id="UpgradeNudge__actionBtn"
            text="View Upgrade"
            @click="openUpgradeModal"
          />
        </div>
      </div>
    </template>
  </Banner>
</template>
<script>
import { mapGetters } from 'vuex';
import moment from 'moment';
import pluralize from 'pluralize';

import {
  eventLocations,
  eventProperties,
} from 'client/GrowthEngineering/utilities/GrowthConstants';
import EventHelper from 'client/GrowthEngineering/EventHelper';
import FeatureTrialsMixin from 'client/GrowthEngineering/utilities/FeatureTrialsMixin';
import MspState from 'client/components/models/MspState';

import {
  Banner,
  Button,
  Icon,
  MarkdownInline,
} from '@jumpcloud/ui-components';

const settingsModule = 'SettingsModule';
const DAYS_REQUIRED_TO_SHOW_NUDGE = 30;

export default {
  name: 'UpgradeNudge',

  EventHelper,

  components: {
    Banner,
    Button,
    Icon,
    MarkdownInline,
  },

  mixins: [FeatureTrialsMixin],

  computed: {
    ...mapGetters('FeatureFlagsModule', ['betaFeatures']),

    ...mapGetters('FeatureTrialModule', [
      'getFeatureUsage',
      'getUpgradeNudgeVisibility',
    ]),

    ...mapGetters(settingsModule, [
      'hasBilling',
    ]),

    action() {
      return this.metaData.modalProps.isAddOn ? 'purchase' : 'upgrade to';
    },

    copy() {
      const daysTrying = `${this.daysSinceTrialStart} ${pluralize('day', this.daysSinceTrialStart)}`;
      const daysSinceTrialCopy = `You have tried ${this.metaData.modalProps.featureName} for ${daysTrying}`;
      const packageType = this.metaData.modalProps.isAddOn ? 'add-on' : 'package';

      return `${daysSinceTrialCopy}. To continue using this feature, ${this.recommendationCopy} ${packageType}.`;
    },

    daysSinceTrialStart() {
      const featureUsage = this.getFeatureUsage(this.metaData.feature);

      const startDate = featureUsage && featureUsage.startDate ? moment(featureUsage.startDate) : moment().format('YYYY-MM-DD');
      const diff = moment().diff(startDate, 'days') + 1;

      return diff;
    },

    hasCurrentTrial() {
      let hasActiveTrial = false;
      const featureUsage = this.getFeatureUsage(this.metaData.modalProps.featureCode);
      if (featureUsage) {
        hasActiveTrial = this.getFeatureUsage(this.metaData.modalProps.featureCode).startDate
          && !this.getFeatureUsage(this.metaData.modalProps.featureCode).endDate;
      }
      return hasActiveTrial;
    },

    metaData() {
      const { feature, modalProps } = this.$route.meta;

      return !feature || !modalProps ? null : { feature, modalProps };
    },

    recommendationCopy() {
      // Note: using featurePackage instead of featurePackageMarkdown until we can find a way to
      // bold and italicize PlatformPlus. See https://jumpcloud.atlassian.net/browse/PU-267
      const feature = this.metaData.modalProps.featurePackage
        || this.metaData.modalProps.featurePackage;
      const copy = `${this.action} the ${feature}`;

      return this.hasBilling
        ? copy
        : `ask an admin with billing to ${copy}`;
    },

    showUpgradeNudge() {
      return this.metaData
        && this.daysSinceTrialStart >= DAYS_REQUIRED_TO_SHOW_NUDGE
        && this.$hasFeatureFlag(this.$featureFlags.upgradeNudge)
        && this.hasCurrentTrial
        && !MspState.isAnMspOrChild;
    },
  },

  methods: {
    openUpgradeModal() {
      this.$options.EventHelper.triggerButtonClicked({
        [eventProperties.description]: 'Upgrade button clicked',
        [eventProperties.location]: eventLocations.upgradeNudge,
        [eventProperties.text]: 'View Upgrade',
      });

      this.$router.push({
        name: 'Upgrade',
      });
    },
  },
};
</script>

<style scoped>
.content {
  align-items: center;
  display: flex;
  justify-content: space-between;
  padding-bottom: var(--jc-spacer-x-small);
}

.infoIcon {
  padding-right: var(--jc-spacer-small);
}

.text {
  font-size: 16px;
}

.textArea {
  align-items: center;
  display: flex;
}

.upgradeNudge {
  --banner-background: var(--jcTertiary50);

  border: 1px solid var(--jcGrey200);
}
</style>
